import { cn } from "@/lib/utils";
import { useState } from "react";
import { LandingCopyBoundary } from "../lib/copy";
import { GrainLowOverlay } from "../lib/grain-low-overlay";
import { GREIGE_GRAIN_SURFACE_CLASS } from "../lib/greige-grain-surface";
import { LANDING_CONTAINER_CLASS, LANDING_SECTION_PAD_CLASS } from "../lib/layout";
import { useLandingLinks } from "../lib/links";
import { hostNavHeightStyle, useHostNavHeight } from "../lib/use-host-nav-height";
import { type TPickerHandoffPhase, shouldPlayPickerIntro } from "./picker-intro";
import { useQuestionnaireFlow } from "./questionnaire-context";
import type { TQuestionnairePath } from "./questionnaire-flow";
import { QuestionnaireFlow } from "./questionnaire-flow-ui";
import { QUESTIONNAIRE_TRANSCRIPT_LIST_CLASS } from "./questionnaire-option-layout";
import { clearQuestionnairePathParam, readQuestionnairePathParam } from "./questionnaire-path";
import { QuestionnairePicker } from "./questionnaire-picker";
import { usePickerHandoff } from "./use-picker-handoff";
import { usePickerIntro } from "./use-picker-intro";

/** Full-page shell mirroring the dialog's chat/options/progress, minus the modal chrome. */
function QuestionnairePageShell({ children }: { children: React.ReactNode }) {
	const navHeight = useHostNavHeight();
	return (
		<div
			className={cn(
				GREIGE_GRAIN_SURFACE_CLASS,
				/* clip, not hidden: overflow-x:hidden computes overflow-y to auto and adds a second scroller */
				"flex min-h-[100svh] w-full flex-col overflow-x-clip font-sans text-teal-500",
			)}
			style={hostNavHeightStyle(navHeight)}
		>
			<GrainLowOverlay />
			<div className="relative z-[1] flex min-h-[100svh] min-w-0 flex-1 flex-col">{children}</div>
		</div>
	);
}

function QuestionnairePageSession({
	path,
	beat,
	motion,
	handoffPhase,
	showStep,
	onSelectPath,
}: {
	path: TQuestionnairePath | null;
	beat: ReturnType<typeof usePickerIntro>["beat"];
	motion: ReturnType<typeof usePickerIntro>["motion"];
	handoffPhase: TPickerHandoffPhase;
	showStep: boolean;
	onSelectPath: (nextPath: TQuestionnairePath) => void;
}) {
	const { stepPanelRef } = useQuestionnaireFlow();

	return (
		<div className="flex min-h-[100svh] flex-1 flex-col">
			<div
				ref={stepPanelRef}
				className={cn(
					"relative min-h-0 flex-1",
					path ? "overflow-visible" : "flex min-h-[100svh] flex-col overflow-hidden",
				)}
			>
				<div
					className={cn(
						path
							? cn(
									LANDING_CONTAINER_CLASS,
									QUESTIONNAIRE_TRANSCRIPT_LIST_CLASS,
									LANDING_SECTION_PAD_CLASS,
									"pb-10",
									showStep && "min-h-[calc(100svh-var(--landing-nav-height,72px)+1px)]",
								)
							: cn(LANDING_CONTAINER_CLASS, "relative flex min-h-0 w-full flex-1 flex-col"),
					)}
				>
					<QuestionnairePicker
						beat={beat}
						motionMode={motion}
						selectedPath={path}
						handoffPhase={handoffPhase}
						onSelect={onSelectPath}
					/>
					{showStep ? (
						<>
							<QuestionnaireFlow.Chat />
							<QuestionnaireFlow.Options />
						</>
					) : null}
				</div>
			</div>
			{showStep ? <QuestionnaireFlow.Progress /> : null}
		</div>
	);
}

function QuestionnairePageContent() {
	const { getFundingHref, getStartedHref, manageCashFlowHref } = useLandingLinks();
	const [path, setPath] = useState<TQuestionnairePath | null>(() => readQuestionnairePathParam());
	const { beat, motion } = usePickerIntro(shouldPlayPickerIntro(path));
	const { phase: handoffPhase, showStep } = usePickerHandoff(path);

	return (
		<QuestionnairePageShell>
			<QuestionnaireFlow.Root
				path={path}
				active={showStep}
				onChangePath={setPath}
				onOpenChange={(open) => {
					if (!open) {
						setPath(null);
						clearQuestionnairePathParam();
					}
				}}
				ctaHrefs={{ getFundingHref, getStartedHref, manageCashFlowHref }}
			>
				<QuestionnairePageSession
					path={path}
					beat={beat}
					motion={motion}
					handoffPhase={handoffPhase}
					showStep={showStep}
					onSelectPath={setPath}
				/>
			</QuestionnaireFlow.Root>
		</QuestionnairePageShell>
	);
}

/**
 * Standalone questionnaire page. `?path=<fund|pay|grow>` deep-links
 * straight into a flow.
 */
export function QuestionnairePage() {
	return (
		<LandingCopyBoundary>
			<QuestionnairePageContent />
		</LandingCopyBoundary>
	);
}
